import type { FileEntry, FolderEntry, StorageEntry } from "../../backend/storage";

export type { FileEntry, FolderEntry, StorageEntry };

export interface ListResponse {
  entries: StorageEntry[];
  path: FolderEntry[];
}

export async function fetchFiles(
  folderId: string | null = null
): Promise<ListResponse> {
  const params = folderId ? `?folderId=${folderId}` : "";
  const res = await fetch(`/api/files${params}`);
  if (!res.ok) throw new Error("Failed to fetch files");
  return res.json();
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export async function uploadFiles(
  files: FileList | File[],
  folderId: string | null = null,
  onProgress?: (progress: UploadProgress) => void
): Promise<FileEntry[]> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("file", file);
  }
  if (folderId) {
    formData.append("folderId", folderId);
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/files");

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress({
          loaded: e.loaded,
          total: e.total,
          percent: Math.round((e.loaded / e.total) * 100),
        });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        reject(new Error("Upload failed"));
      }
    };

    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(formData);
  });
}

export async function createFolder(
  displayName: string,
  parentId: string | null = null
): Promise<FolderEntry> {
  const res = await fetch("/api/folders", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ displayName, parentId }),
  });
  if (!res.ok) throw new Error("Failed to create folder");
  return res.json();
}

export async function renameFile(
  id: string,
  displayName: string
): Promise<StorageEntry> {
  const res = await fetch(`/api/files/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ displayName }),
  });
  if (!res.ok) throw new Error("Rename failed");
  return res.json();
}

export async function moveEntry(
  id: string,
  parentId: string | null
): Promise<StorageEntry> {
  const res = await fetch(`/api/files/${id}/move`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ parentId }),
  });
  if (!res.ok) throw new Error("Move failed");
  return res.json();
}

export async function deleteFile(id: string): Promise<void> {
  const res = await fetch(`/api/files/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Delete failed");
}

export async function replaceFile(id: string, file: File): Promise<FileEntry> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`/api/files/${id}/replace`, {
    method: "PUT",
    body: formData,
  });
  if (!res.ok) throw new Error("Replace failed");
  return res.json();
}

export async function setFolderIcon(folderId: string, file: File | Blob): Promise<FolderEntry> {
  const formData = new FormData();
  formData.append("icon", file);
  const res = await fetch(`/api/folders/${folderId}/icon`, {
    method: "PUT",
    body: formData,
  });
  if (!res.ok) throw new Error("Set icon failed");
  return res.json();
}

export async function removeFolderIcon(folderId: string): Promise<FolderEntry> {
  const res = await fetch(`/api/folders/${folderId}/icon`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Remove icon failed");
  return res.json();
}
